// React Dependencies
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Autocomplete } from '@material-ui/lab';
import { toast } from 'react-toastify';
import { TextField } from '@material-ui/core';

// Internal Dependencies
import { IsEmpty } from '../../../common/checks';

const defaultValue = { id: null, name: '' };

/**
 * Generic suggestion field component
 * @param {Function} fetchSuggestions - API function to fetch suggestions
 * @param {string} placeholder - Placeholder text for the input field
 * @param {boolean} disabled - Whether the field is disabled
 * @param {Object} value - Current selected value
 * @param {Function} onChange - Callback when value changes
 * @param {Object} apiParams - Additional parameters to pass to the API function
 * @param {React.Ref} ref - Ref for imperative methods
 */
const SuggestionField = forwardRef(({
  fetchSuggestions,
  placeholder = 'Select option',
  disabled = false,
  value = null,
  onChange,
  apiParams = {},
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [inputTimeout, setInputTimeout] = useState(null);

  const fetchOptions = useCallback(async (key = '') => {
    try {
      const results = await fetchSuggestions({ key, ...apiParams });
      const mappedOptions = results.map((item) => ({
        id:   item.id,
        name: item.name,
      }));
      setOptions(mappedOptions);
    } catch (err) {
      toast.error(err.message);
    }
  }, [fetchSuggestions, apiParams]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }
    const timeout = setTimeout(() => {
      fetchOptions(newInputValue);
    }, 400);
    setInputTimeout(timeout);
  };

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      onChange(defaultValue);
      setOptions([]);
      setInputValue('');
    },
  }));

  return (
    <Autocomplete
      inputValue={inputValue}
      freeSolo
      size="small"
      disabled={disabled}
      options={options}
      value={value}
      getOptionLabel={(option) => (!IsEmpty(option && option.name) ? option.name : '')}
      onInputChange={handleInputChange}
      onChange={(event, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            style: { fontSize: 13 },
          }}
        />
      )}
    />
  );
});

SuggestionField.propTypes = {
  fetchSuggestions: PropTypes.func.isRequired,
  placeholder:      PropTypes.string,
  disabled:         PropTypes.bool,
  value:            PropTypes.shape({
    id:   PropTypes.string,
    name: PropTypes.string,
  }),
  onChange:  PropTypes.func.isRequired,
  apiParams: PropTypes.shape({}),
};

SuggestionField.defaultProps = {
  placeholder: 'Select option',
  disabled:    false,
  value:       null,
  apiParams:   {},
};

export default SuggestionField;
